import React, { useCallback, useState, useEffect } from 'react';
import { Grid, TextField } from '@material-ui/core';
import {
  verifyDuplicateExamSheetName,
  verifyDuplicateExamSheetCode,
} from '../../services/validation/validation.service';
import ActionButtons from './ActionButtons';
import EditButton from './EditButton.component';
import AddSessionItemButton from './NewItemButton.component';
import {
  createExamSheet,
  getExamGradeRuleFromExamSheet,
  modifyExamSheet,
  DeleteExamSheetsAttachment,
  getCanExamSheetBeConfigured,
  setExamSheetAsConfigured,
} from '../../services/exams/sessions/sessions.service';
import { DetailsProps } from './DetailsProps.types';
import FileDragAndDrop from '../FileDrag&Drop/FileDragAndDrop.component';
import useExamSessionValidation from '../../hooks/useExamSessionValidation';
import { Prompt, SubText } from '../Prompt/Prompt.component';
import {
  extractFileType,
  FileType,
  getExtFromFileName,
  getFileTypeFromExtension,
  MAX_FILE_SIZE,
} from '../../utils/fileProcessing';
import {
  IExamSheetDetails,
  IIdResult,
  CanExamSheetBeConfiguredResults,
  examSheetValidationMessage,
  examSheetValidationMessages,
} from '../../models/session.types';
import { WorkMode, NewTreeItemId } from '../SessionTree/SessionTree.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { useLayoutContext } from '../../Context/Layout.context';
import EnvWindow from '../../utils/env';
import { AxiosResponse } from 'axios';
import { useStyles as sessionDetails } from './SessionDetails.styles';
import { useStyles } from './ExamSheetDetails.styles';
import { ConfirmButton } from '../buttons/ConfirmButton.component';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Button from '@material-ui/core/Button';
import CheckIcon from '@mui/icons-material/Check';
import DeleteButton from './DeleteButton.component';
import { blockIconForInput } from './BlockItemForInput';
import SwitchOne from '../SwitchOne/SwitchOne.component';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import useSessionUser from '../../hooks/useSessionUser';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';

interface ExamSheetDetailsProps extends DetailsProps {
  data: IExamSheetDetails;
  isReadOnly: () => boolean;
  onDelete: () => void;
}

const MAX_RETENTION_VALUE = 999;

const ExamSheetDetails: React.FC<ExamSheetDetailsProps> = (props) => {
  const classes = useStyles();
  const sessionDetailsClasses = sessionDetails();
  const { userAssessmentSessionInfo } = useSessionUser();
  const [examSheetName, setExamSheetName] = useState<string>(props.data.name);
  const [examSheetCode, setExamSheetCode] = useState<string | undefined>(props.data.code);
  const [retentionPeriod, setRetentionPeriod] = useState<string>(
    props.data.retentionPeriod ? props.data.retentionPeriod.toString() : '',
  );
  const [isRetentionPeriodEnabled, setIsRetentionPeriodEnabled] = useState<boolean>(!!props.data.retentionPeriod);
  const [isFieldNameEdited, setIsFieldNameEdited] = useState<boolean>(false);
  const [isBlurName, setIsBlurName] = useState<boolean>(false);
  const [isFieldCodeEdited, setIsFieldCodeEdited] = useState<boolean>(false);
  const [isBlurCode, setIsBlurCode] = useState<boolean>(false);
  const [rulesFile, setRulesFile] = useState<File | undefined>();
  const [isAddedFile, setIsAddedFile] = useState<boolean>(false);
  const [isThereAnyFile] = useState<boolean>(
    props.data.gradeRulesImageFilePath !== null && props.data.gradeRulesImageFilePath !== undefined,
  );
  const [isFileLoader, setIsFileLoader] = useState<boolean>(false);
  const [isFileToReload, setIsFileToReload] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isProblemWithFile, setIsProblemWithFile] = useState<boolean>(false);
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorSubTexts, setErrorSubTexts] = useState<SubText[]>();
  const [canExamSheetBeConfiguredResults, setCanExamSheetBeConfiguredResults] = useState<
    CanExamSheetBeConfiguredResults | undefined
  >(undefined);
  const [isExamSheetFullyConfigured, setIsExamSheetFullyConfigured] = useState<boolean>(false);
  const [isExamSheetFinishConfigurationWarningVisible, setIsExamSheetFinishConfigurationWarningVisible] =
    useState<boolean>(false);
  const [isValidExamSheetCode, setIsValidExamSheetCode] = useState<boolean>(true);
  const [isValidRetentionPeriod, setIsValidRetentionPeriod] = useState<boolean>(true);

  const { openAlert, showLoader } = useLayoutContext();
  const link = `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${props.data.id}/document`;

  const hasAdminSessionStatusManagePermission = usePermission(UserPermission.AdminSessionStatusManage);

  const { isValidValue: validExamSheetName } = useExamSessionValidation({
    isEdited: isFieldNameEdited,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: !examSheetName ? examSheetName : examSheetName.trim(),
    data: !props.data.name ? props.data.name : props.data.name.trim(),
    verificationApi: useCallback(
      (value: string) => verifyDuplicateExamSheetName(props.data.examId ?? '', value),
      [props.data.examId],
    ),
    onBlur: isBlurName,
  });

  const { isValidValue: validExamSheetCode } = useExamSessionValidation({
    isEdited: isFieldCodeEdited,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: !examSheetCode ? '' : examSheetCode.trimEnd(),
    data: !props.data.code ? '' : props.data.code.trimEnd(),
    verificationApi: useCallback(
      (value: string) => verifyDuplicateExamSheetCode(props.data.examId ?? '', value),
      [props.data.examId],
    ),
    onBlur: isBlurCode,
  });

  useEffect(() => {
    if (!(isThereAnyFile && props.data.id !== NewTreeItemId.ExamSheet)) return;

    const controller = new AbortController();
    setIsFileLoader(true);
    getExamGradeRuleFromExamSheet(props.data.id.toString(), controller.signal)
      .then((response) => {
        const fileExt = getExtFromFileName(props.data.gradeRulesImageFilePath ?? '');
        const image = new File([response.data], props.data.gradeRulesImageFilePath ?? '', {
          type: getFileTypeFromExtension(fileExt) ?? 'image/png',
        });
        setRulesFile(image);
        setIsAddedFile(true);
        setIsDisabled(true);
      })
      .catch((e) => {
        if (e.response.status !== 404) {
          console.error('Error while fetching Exam Grade Rules sheet', e);
        }
      })
      .finally(() => {
        setIsFileLoader(false);
        setIsFileToReload(false);
      });

    return () => {
      controller?.abort();
    };
  }, [isThereAnyFile, props.data.id, isFileToReload]);

  const acceptedFileExt = ['pdf', 'xls', 'xlsx'];
  const acceptedFileTypes: FileType[] = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  const handleFilesUploaded = useCallback(
    (files: FileList) => {
      files[0].arrayBuffer().then((buffer) => {
        const extractedType = extractFileType(buffer);
        if (files[0].size <= MAX_FILE_SIZE) {
          if (files[0].type === extractedType && acceptedFileTypes.includes(extractedType)) {
            setIsProblemWithFile(false);
            setRulesFile(files[0]);
            setIsDisabled(false);
          } else {
            setErrorTitle('Nieprawidłowy format pliku');
            setErrorSubTexts([{ text: 'Akceptowany format załączników to: ' }, { text: acceptedFileExt.join(', ') }]);
            setIsProblemWithFile(true);
            setIsDisabled(true);
            console.error(`Format pliku deklarowany: ${files[0].type} odczytany: ${extractedType}`);
          }
        } else {
          setErrorTitle('Wybrany plik jest za duży');
          setErrorSubTexts([
            { text: 'Maksymalny rozmiar załączników to: ' },
            { text: `${MAX_FILE_SIZE / 1024 / 1024}MB` },
          ]);
          setIsProblemWithFile(true);
          setIsDisabled(true);
        }
      });
    },
    [rulesFile],
  );

  const handleRemoveFile = useCallback(() => {
    setRulesFile(undefined);
    setIsAddedFile(true);
    setIsDisabled(false);
  }, [rulesFile]);

  const revert = () => {
    setExamSheetName(props.data.name);
    setExamSheetCode(props.data.code);
    setIsRetentionPeriodEnabled(!!props.data.retentionPeriod);
    setRetentionPeriod(props.data.retentionPeriod ? props.data.retentionPeriod.toString() : '');
    setRulesFile(undefined);
    if (isThereAnyFile) {
      setIsFileToReload(true);
    }
  };

  const closeHandle = () => {
    props.onClose(props.data, BreadcrumbLevel.EXAM, revert);
  };

  const handleEditMode = () => {
    props.onEdit(props.data);
  };

  useEffect(() => {
    if (retentionPeriod && !retentionPeriod.length) return;
    if (
      retentionPeriod &&
      (parseInt(retentionPeriod) < 0 ||
        parseInt(retentionPeriod) > MAX_RETENTION_VALUE ||
        isNaN(Number(retentionPeriod)))
    ) {
      setIsValidRetentionPeriod(false);
    } else setIsValidRetentionPeriod(true);
  }, [retentionPeriod]);

  useEffect(() => {
    if (!isRetentionPeriodEnabled) {
      setRetentionPeriod('');
    }
  }, [isRetentionPeriodEnabled]);

  useEffect(() => {
    if (
      !validExamSheetName ||
      !validExamSheetCode ||
      (examSheetName === props.data.name &&
        examSheetCode === props.data.code &&
        retentionPeriod == props.data.retentionPeriod) ||
      !examSheetCode ||
      !examSheetName ||
      isProblemWithFile ||
      ((retentionPeriod === '' || retentionPeriod === null) && isRetentionPeriodEnabled) ||
      (isRetentionPeriodEnabled && parseInt(retentionPeriod ?? '0') > MAX_RETENTION_VALUE)
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [
    validExamSheetName,
    validExamSheetCode,
    examSheetName,
    examSheetCode,
    props.data,
    isProblemWithFile,
    retentionPeriod,
    isRetentionPeriodEnabled,
  ]);

  const isTextFieldDisabled = () => {
    if (props.workMode === WorkMode.ADD) {
      return false;
    } else {
      return props.isSessionImported || props.workMode !== WorkMode.EDIT || props.data.isConfigured;
    }
  };

  const saveChanges = async () => {
    if (props.data.id !== NewTreeItemId.ExamSheet) {
      await modifyExamSheet(
        props.data.id,
        examSheetCode ?? '',
        examSheetName,
        rulesFile,
        retentionPeriod === '' ? null : retentionPeriod,
      ).then(
        (result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          setIsValidExamSheetCode(true);
          if (!!rulesFile == false && isAddedFile) {
            DeleteExamSheetsAttachment(props.data.id).then(
              () => {
                props.setWorkMode(WorkMode.VIEW);
                props.onSave(result.data, false);
              },
              () => {
                props.setWorkMode(WorkMode.VIEW);
                props.onSave(result.data, false);
              },
            );
          } else {
            props.setWorkMode(WorkMode.VIEW);
            props.onSave(result.data, false);
          }
        },
        (error) => {
          if (error.response.status === 400) {
            setIsValidExamSheetCode(false);
          }
          openAlert('error');
        },
      );
    } else {
      await createExamSheet(props.data.parentId, examSheetCode ?? '', examSheetName, rulesFile).then(
        (result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          setIsValidExamSheetCode(true);
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, true);
        },
        (error) => {
          if (error.response.status === 400) {
            setIsValidExamSheetCode(false);
          }
          openAlert('error');
        },
      );
    }
  };

  const verifyExamSheetConfiguration = () => {
    showLoader(true, 'Weryfikuję dane');
    getCanExamSheetBeConfigured(props.data.id).then(
      (response: AxiosResponse<CanExamSheetBeConfiguredResults>) => {
        setCanExamSheetBeConfiguredResults(response.data);
        showLoader(false);
        openAlert('success', 'Zweryfikowano dane');
      },
      () => {
        showLoader(false);
      },
    );
  };

  const confirmExamSheetConfigurationFinish = () => {
    setCanExamSheetBeConfiguredResults(undefined);
    setIsExamSheetFinishConfigurationWarningVisible(true);
  };

  const handleConfirmConfigurationWarning = () => {
    showLoader(true, 'Trwa ustawienie konfiguracji arkusza');
    setIsExamSheetFinishConfigurationWarningVisible(false);
    setExamSheetAsConfigured(props.data.id)
      .then(
        () => {
          openAlert('success', 'Pomyślnie zakończono konfigurację arkusza');
          props.onSave({ id: props.data.id }, false);
        },
        () => {
          openAlert('error', 'Konfiguracja arkusza nie powiodła się. Spróbuj ponownie później');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  useEffect(() => {
    let result = true;
    if (canExamSheetBeConfiguredResults) {
      let key: keyof CanExamSheetBeConfiguredResults;
      for (key in canExamSheetBeConfiguredResults) {
        result = result && canExamSheetBeConfiguredResults[key];
      }
    }
    setIsExamSheetFullyConfigured(result);
  }, [canExamSheetBeConfiguredResults]);

  const handleRetentionInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = event.target.value.trim();
    if (newValue === '') {
      setRetentionPeriod(newValue);
      return;
    }

    const isAnyNotNumber = newValue.split('').filter((char) => Number.isNaN(Number(char as unknown)));

    if (isAnyNotNumber.length > 0) return;
    setRetentionPeriod(newValue);
  };

  const getRetentionErrorText = (): string | undefined => {
    if (props.workMode !== WorkMode.VIEW) {
      if (!isValidRetentionPeriod && (retentionPeriod?.length ?? 0 > 0))
        return `Liczba dni powina wynosić od 1 do ${MAX_RETENTION_VALUE}`;
      else if ((retentionPeriod?.length ?? 0) === 0) return 'Pole nie może być puste';
    }
    return undefined;
  };

  const acceptedExtension = ['pdf', 'xls', 'xlsx'];

  return (
    <>
      {props.data && (
        <Grid container alignContent="space-between" style={{ height: '490px' }}>
          <Grid container justifyContent="flex-end" alignContent="center">
            {props.workMode !== WorkMode.EDIT ? (
              <EditButton workMode={props.workMode} handleEditMode={handleEditMode} isDisabled={props.isReadOnly()} />
            ) : (
              <DeleteButton
                isDisabled={props.isReadOnly() || props.data.isConfigured}
                workMode={props.workMode}
                handleDeleteMode={props.onDelete}
              />
            )}
          </Grid>
          <Grid
            container
            justifyContent="center"
            alignContent="center"
            style={{
              maxHeight: '390px',
              overflowX: 'auto',
              paddingTop: props.workMode === WorkMode.EDIT ? '100px' : '10px',
              transform: 'translateY(-30px)',
            }}
          >
            <Grid container justifyContent="center" className={sessionDetailsClasses.headerRow}>
              {props.workMode === WorkMode.VIEW && <div className={sessionDetailsClasses.headerText}>Arkusz</div>}
              {props.workMode === WorkMode.EDIT && (
                <div className={sessionDetailsClasses.headerText}>Edycja arkusza</div>
              )}
              {props.workMode === WorkMode.ADD && (
                <div className={sessionDetailsClasses.headerText}>Zdefiniuj arkusz</div>
              )}
            </Grid>

            <TextField
              className={sessionDetailsClasses.formInput}
              value={examSheetCode}
              onChange={(e) => {
                setExamSheetCode(e.target.value.trimStart());
                setIsFieldCodeEdited(true);
                setIsBlurCode(false);
              }}
              onBlur={() => {
                setIsBlurCode(true);
              }}
              id="Kod*"
              label="Kod*"
              variant="outlined"
              disabled={isTextFieldDisabled()}
              InputProps={{
                startAdornment: blockIconForInput(isTextFieldDisabled()),
              }}
              error={
                (props.workMode !== WorkMode.VIEW && !validExamSheetCode && validExamSheetCode !== undefined) ||
                !isValidExamSheetCode
              }
              inputProps={{
                style: {
                  padding: '15.5px',
                  color: props.isSessionImported ? 'unset' : '#2C2D3E',
                },
              }}
              InputLabelProps={{
                style: { color: '#2C2D3E' },
              }}
              FormHelperTextProps={{
                style: {
                  fontSize: '11px',
                  color: '#BC0000',
                },
              }}
              helperText={
                props.workMode !== WorkMode.VIEW && !validExamSheetCode && validExamSheetCode !== undefined
                  ? 'Pole "Kod" musi być unikatowe dla sesji egzaminacyjnej'
                  : !isValidExamSheetCode
                  ? 'Kod arkusza musi być unikatowy w systemie'
                  : undefined
              }
            />
            <TextField
              className={sessionDetailsClasses.formInput}
              value={examSheetName}
              onChange={(e) => {
                setExamSheetName(e.target.value.trimStart());
                setIsFieldNameEdited(true);
                setIsBlurName(false);
              }}
              onBlur={() => {
                setIsBlurName(true);
              }}
              id="Nazwa*"
              label="Nazwa*"
              variant="outlined"
              disabled={isTextFieldDisabled()}
              error={props.workMode !== WorkMode.VIEW && !validExamSheetName}
              InputProps={{
                startAdornment: blockIconForInput(isTextFieldDisabled()),
              }}
              inputProps={{
                style: {
                  padding: '15.5px',
                  color: props.isSessionImported ? 'unset' : '#2C2D3E',
                },
              }}
              InputLabelProps={{
                style: { color: '#2C2D3E' },
              }}
              FormHelperTextProps={{
                style: {
                  fontSize: '11px',
                  color: '#BC0000',
                },
              }}
              helperText={
                props.workMode !== WorkMode.VIEW && !validExamSheetName
                  ? 'Pole "Nazwa" musi być unikatowe dla sesji egzaminacyjnej'
                  : undefined
              }
            />

            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              direction="row"
              className={classes.retentionRow}
            >
              <div className={classes.retentionText}>Usunięcie sesji po określonej liczbie dni</div>
              <TooltipOne
                title={
                  isRetentionPeriodEnabled
                    ? 'Usunięcie sesji po określonej liczbie dni'
                    : 'Sesja oceniania może zostać usunięta w dowolnym momencie'
                }
                placement="top"
              >
                <span>
                  <SwitchOne
                    disableRipple
                    checked={isRetentionPeriodEnabled}
                    color={'primary'}
                    disabled={
                      props.workMode !== WorkMode.EDIT || userAssessmentSessionInfo.function === ExaminerFunction.KK
                    }
                    inputProps={{
                      'aria-label': 'Usunięcie sesji po określonej liczbie dni',
                    }}
                    onChange={() => setIsRetentionPeriodEnabled(!isRetentionPeriodEnabled)}
                  />
                </span>
              </TooltipOne>
            </Grid>

            {isRetentionPeriodEnabled && (
              <TextField
                className={sessionDetailsClasses.formInput}
                value={retentionPeriod ?? ''}
                onChange={handleRetentionInputChange}
                id="Liczba dni, po których można usunąć sesję*"
                label="Liczba dni, po których można usunąć sesję*"
                variant="outlined"
                disabled={
                  props.workMode !== WorkMode.EDIT || userAssessmentSessionInfo.function === ExaminerFunction.KK
                }
                error={
                  (props.workMode !== WorkMode.VIEW && !isValidRetentionPeriod) ||
                  retentionPeriod === '' ||
                  Number.isNaN(retentionPeriod)
                }
                InputProps={{
                  startAdornment: blockIconForInput(props.workMode !== WorkMode.EDIT),
                }}
                inputProps={{
                  style: {
                    padding: '15.5px',
                    color: props.isSessionImported ? 'unset' : '#2C2D3E',
                  },
                }}
                InputLabelProps={{
                  style: { color: '#2C2D3E' },
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
                helperText={getRetentionErrorText()}
              />
            )}

            <Grid container justifyContent="flex-start" direction="column" className={classes.fileWrapper}>
              <FileDragAndDrop
                handleFilesUpload={handleFilesUploaded}
                acceptedExtension={acceptedExtension}
                handleRemoveFile={handleRemoveFile}
                ruleFile={rulesFile}
                fileSize={rulesFile?.size}
                fileName={rulesFile?.name}
                isFileLoader={isFileLoader}
                isEditMode={props.workMode !== WorkMode.VIEW}
                id={props.data.id}
                link={link}
              />
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
            style={{ transform: 'translateY(-30px)' }}
          >
            {hasAdminSessionStatusManagePermission && !props.data.isConfigured && (
              <ConfirmButton
                className={classes.finishConfigurationSheetButton}
                disabled={props.workMode === WorkMode.ADD}
                onClick={verifyExamSheetConfiguration}
              >
                ZAKOŃCZ KONFIGURACJĘ
              </ConfirmButton>
            )}
            {props.data.isConfigured && (
              <Button className={classes.sheetConfiguredButton} startIcon={<CheckIcon />} variant="text">
                ARKUSZ SKONFIGUROWANY
              </Button>
            )}
            {props.workMode === WorkMode.VIEW && (
              <AddSessionItemButton
                caption="Dodaj grupę zadań"
                disabled={props.data.isConfigured}
                onClick={() => props.onAddChild(BreadcrumbLevel.TASKGROUP, props.data.id, props.data.id)}
              />
            )}

            {props.workMode !== WorkMode.VIEW && (
              <ActionButtons disable={isDisabled} saveHandle={saveChanges} closeHandle={closeHandle} />
            )}
          </Grid>
        </Grid>
      )}

      {isProblemWithFile && (
        <Prompt
          type={'error'}
          open={isProblemWithFile}
          mainText={errorTitle}
          subTexts={errorSubTexts}
          onConfirm={() => setIsProblemWithFile(false)}
        />
      )}
      {canExamSheetBeConfiguredResults && (
        <Prompt
          type={'warning'}
          open={true}
          size="sm"
          mainActionButtonText={isExamSheetFullyConfigured ? 'Potwierdź' : 'OK'}
          onCancel={isExamSheetFullyConfigured ? () => setCanExamSheetBeConfiguredResults(undefined) : undefined}
          onConfirm={
            isExamSheetFullyConfigured
              ? confirmExamSheetConfigurationFinish
              : () => setCanExamSheetBeConfiguredResults(undefined)
          }
          mainText={'By zakończyć konfigurację arkusza muszą zostać spełnione poniższe kryteria:'}
          subTexts={[
            {
              text: (
                <ul className={classes.examSheetBeConfiguredResultsList}>
                  {examSheetValidationMessages.map((message: examSheetValidationMessage) => {
                    return (
                      <li key={message.propName}>
                        {canExamSheetBeConfiguredResults[message.propName] ? (
                          <CheckCircleIcon className={classes.successIcon} />
                        ) : (
                          <CancelIcon className={classes.errorIcon} />
                        )}
                        {message.message}
                      </li>
                    );
                  })}
                </ul>
              ),
            },
          ]}
        />
      )}
      {isExamSheetFinishConfigurationWarningVisible && (
        <Prompt
          type={'confirmation'}
          open={true}
          size="sm"
          mainActionButtonText={'TAK'}
          onCancel={() => setIsExamSheetFinishConfigurationWarningVisible(false)}
          onConfirm={handleConfirmConfigurationWarning}
          withHorizontalLineUnderMainText
          mainText={'Czy na pewno chcesz zakończyć konfigurację arkusza?'}
          subTexts={[
            {
              text: (
                <div className={classes.examSheetBeConfiguredWarningList}>
                  <div className={classes.warningHeader}>Zakończenie konfiguracji arkusza zablokuje:</div>
                  <ul>
                    <li>Możliwość zmiany struktury GZ w arkuszu (przenoszenie GZ)</li>
                    <li>Możliwość dodawania, usuwania i edycji zadań</li>
                    <li>Możliwość dodawanie nowej GZ w tym arkuszu</li>
                    <li>Możliwość zmiany, edycji, usuwania schematów punktowania</li>
                  </ul>
                </div>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

export default ExamSheetDetails;
